import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import { IoChevronDown } from "react-icons/io5";
import Header from "./Header";
import "./Sidebar.css";
import axiosInstance from "../axiosinstance";
import Select, { components as selectComponents } from "react-select";
import { METHOD_STATEMENT_URL } from "../Config";

import { useNavigate } from "react-router-dom";

const ProjectSingleValue = (props) => (
  <selectComponents.SingleValue {...props}>
    <span title={props.data?.label || ""}>{props.data?.label}</span>
     
  </selectComponents.SingleValue>
);

const ProjectDropdownIndicator = (props) => (
  <selectComponents.DropdownIndicator {...props}>
    <IoChevronDown className="project-select__dropdown-chevron" />
  </selectComponents.DropdownIndicator>
);

// ---- Module-scope helpers (pure, no component state) ----
const normalizeIconKey = (value = "") =>
  String(value).trim().toLowerCase().replace(/\s+/g, "-");

const LOCAL_SIDEBAR_ICON_FILES = new Set([
  "activity.png", "all-flanges.png", "attendance.png", "clients.png", "dashboard.png",
  "departments.png", "equipments.png", "equipmentsgroup.png", "group-assignments.png",
  "lookahead.png", "notification.png", "participant.png", "project.png", "project_document.png",
  "report.png", "safety.png", "settings.png", "system-user.png", "tickets.png", "timeline.png",
]);

const getSidebarIconFile = (item = {}) => {
  const fileNameMap = {
    dashboard: "dashboard.png",
    "group-assignments": "group-assignments.png",
    report: "report.png",
    notification: "notification.png",
    project: "project.png",
    projects: "project.png",
    tickets: "tickets.png",
    clients: "clients.png",
    "system-user": "system-user.png",
    "system-users": "system-user.png",
    flanges: "all-flanges.png",
    "flange-dashboard": "all-flanges.png",
    equipments: "equipments.png",
    "equipment-dashboard": "equipments.png",
    departments: "departments.png",
    designations: "departments.png",
    equipmentsgroup: "equipmentsgroup.png",
    "equipment-group": "equipmentsgroup.png",
    activity: "activity.png",
    "my-activity": "activity.png",
    attendance: "attendance.png",
    "project-documents": "report.png",
    project_documents: "report.png",
    "project-document": "report.png",
    project_document: "project_document.png",
    "event-participants": "participant.png",
    "look-ahead": "lookahead.png",
    timeline: "timeline.png",
    rundown: "timeline.png",
    "the-rundown": "timeline.png",
    safety: "safety.png",
    "safety.png": "safety.png",
    "safety-daily-summary": "safety.png",
    "safety-daily": "safety.png",
  };

  const backendFileName = String(item.icons || "").trim().split(/[\\/]/).pop().toLowerCase();
  if (LOCAL_SIDEBAR_ICON_FILES.has(backendFileName)) return backendFileName;

  const iconKey = normalizeIconKey(item.icons);
  const moduleKey = normalizeIconKey(item.module_name);
  return fileNameMap[iconKey] || fileNameMap[moduleKey] || "report.png";
};

const resolveIconPath = (item) => `/assests/icons/${getSidebarIconFile(item)}`;

// Safe accessor for a menu item's name (never throws on null/undefined).
const getMenuName = (item) => item?.module_name || "";

// Centralized label display (e.g. Departments -> Designations). Null-safe.
const getDisplayLabel = (moduleName) =>
  String(moduleName || "").toLowerCase() === "departments"
    ? "Designations"
    : moduleName || "";

const isMethodStatement = (moduleName = "") => {
  const normalized = String(moduleName).trim().toLowerCase();
  return normalized === "method statement" || normalized === "project documents";
};

// ---- Tree-based, boundary-safe active-route matching ----
// Trailing slashes stripped, case-insensitive. Dashes are PRESERVED
// (so "/flange-report" never collides with "/flangereport").
const normalizeRoute = (value = "") => {
  if (!value) return "";
  return value.replace(/\/+$/, "").toLowerCase();
};

// The backend menu may still expose the legacy Equipment Dashboard route.
// Keep the UI canonical while backend menu records are migrated separately.
const resolveMenuRoute = (node = {}) => {
  const moduleName = String(node.module_name || "").trim().toLowerCase();
  const route = node.routes || "";
  if (moduleName === "equipment dashboard" || normalizeRoute(route) === "/dashboard") {
    return "/equipment-dashboard";
  }
  return route;
};

// True only on exact match or a real path-segment boundary.
// "/reports" is NOT active when the route is "/report" (no "/report/" prefix).
const isRouteActive = (pathname, route) => {
  if (!route) return false;
  const current = normalizeRoute(pathname);
  const target = normalizeRoute(route);
  if (!target) return false;
  return current === target || current.startsWith(target + "/");
};

// Hidden page routes (add/edit/view forms) that are NOT menu items but should
// activate their parent menu. Keyed by the PARENT'S backend route (normalized,
// lowercase), NOT by module_name. isRouteActive normalizes both sides, so the
// values can be written in any casing.
// Keys are PARENT backend routes, normalized lowercase (lookup uses
// normalizeRoute). Values are hidden page routes; isRouteActive matches them at
// the "/" boundary, so an :id suffix (e.g. "/EditFlanges/123") still matches.
const relatedRouteMap = {
  // System Users (backend route: "/system-users")
  "/system-users": ["/User-Form", "/Edit-user-form", "/View-user-form"],

  // Flanges (backend route: "/flanges")
  "/flanges": [
    "/create-flange",
    "/EditFlanges",
    "/flange-Forms",
    "/Flange_history",
    "/view-tag-info",
    "/flange-checklist",
  ],

  // Tickets (backend route: "/tickets" / "/Tickets")
  "/tickets": ["/TicketDetails"],

  // Equipments (backend route: "/equipments")
  "/equipments": ["/equipment-details"],

  // Projects — both spellings, since backend route is "/project" (singular)
  // while App.js registers "/Projects".
  "/projects": ["/project-form", "/Edit-project-form"],
  "/project": ["/project-form", "/Edit-project-form"],
};

// True if the current pathname is one of the hidden routes mapped to parentRoute.
const isRelatedRouteActive = (pathname, parentRoute) => {
  if (!parentRoute) return false;
  const relatedRoutes = relatedRouteMap[normalizeRoute(parentRoute)] || [];
  return relatedRoutes.some((route) => isRouteActive(pathname, route));
};

// True if this node's route, a hidden related route, OR any descendant matches.
const isSubtreeActive = (node, pathname) => {
  if (!node) return false;
  const resolvedRoute = resolveMenuRoute(node);
  if (isRouteActive(pathname, resolvedRoute)) return true;
  if (isRelatedRouteActive(pathname, resolvedRoute)) return true;
  return Array.isArray(node.children)
    ? node.children.some((child) => isSubtreeActive(child, pathname))
    : false;
};

/**
 * Recursive sidebar menu node.
 * - Branch (has children): renders a group-header button that expands/collapses
 *   its children. It never navigates, even if it has a route.
 * - Leaf with a route: renders a <Link> using node.routes directly.
 * - Leaf without a route and without children: renders nothing.
 *
 * `isExpanded` is the SIDEBAR rail state (controls label/chevron visibility).
 * The local `open` state is the per-group SUBMENU state — they are independent.
 */
function MenuNode({ node, depth = 0, isExpanded, location }) {
  const [open, setOpen] = useState(false);

  const children = Array.isArray(node.children) ? node.children : [];
  const hasChildren = children.length > 0;
  const label = getDisplayLabel(node.module_name);
  const iconPath = resolveIconPath(node);
  const resolvedRoute = resolveMenuRoute(node);

  // Tree-based active state (no hardcoded childPaths).
  // selfActive also covers hidden form routes (add/edit/view) mapped to this node.
  const selfActive =
    isRouteActive(location.pathname, resolvedRoute) ||
    isRelatedRouteActive(location.pathname, resolvedRoute);
  const childActive = hasChildren
    ? children.some((child) => isSubtreeActive(child, location.pathname))
    : false;
  // Auto-open a group when the current route lives under it (incl. nested).
  // Only opens — never force-closes a group the user opened/closed manually.
  useEffect(() => {
    if (childActive) {
      setOpen(true);
    }
  }, [childActive]);

  // Only indent when the rail is expanded; collapsed rail keeps icons aligned.
  const indentStyle =
    isExpanded && depth > 0 ? { paddingLeft: `${12 + depth * 16}px` } : undefined;

  // Branch node -> expandable group header, no navigation.
  if (hasChildren) {
    return (
      <div className="sidebar-group">
        <button
          type="button"
          className={`sidebar-item sidebar-group-header ${
            childActive ? "has-active-child" : ""
          } ${open ? "open" : ""}`}
          style={indentStyle}
          onClick={() => setOpen((prev) => !prev)}
          aria-expanded={open}
          title={label}
        >
          <img
            className="sidebar-item-icon"
            src={iconPath}
            alt={`${label} icon`}
          />
          {isExpanded && <span className="sidebar-item-label">{label}</span>}
          {isExpanded && (
            <IoChevronDown
              className={`sidebar-group-chevron ${open ? "open" : ""}`}
            />
          )}
        </button>

        {open && (
          <div className="sidebar-submenu">
            {children.map((child) => (
              <MenuNode
                key={child.menu_id}
                node={child}
                depth={depth + 1}
                isExpanded={isExpanded}
                location={location}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  // Leaf: preserve external "method statement" links.
  if (isMethodStatement(node.module_name)) {
    return (
      <a
        href={METHOD_STATEMENT_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="sidebar-item"
        style={indentStyle}
        title={label}
      >
        <img
          className="sidebar-item-icon"
          src={iconPath}
          alt={`${label} icon`}
        />
        {isExpanded && <span className="sidebar-item-label">{label}</span>}
      </a>
    );
  }

  // Leaf without a route and without children -> skip safely.
  if (!resolvedRoute) {
    return null;
  }

  // Leaf with a real route -> navigate using node.routes directly.
  // Boundary-safe match: "/report" stays inactive when path is "/reports".
  return (
    <Link
      to={resolvedRoute}
      className={`sidebar-item ${selfActive ? "active" : ""}`}
      style={indentStyle}
      title={label}
    >
      <img className="sidebar-item-icon" src={iconPath} alt={`${label} icon`} />
      {isExpanded && <span className="sidebar-item-label">{label}</span>}
    </Link>
  );
}

function Sidebar({ onSidebarToggle = () => {}, onLogout, authMeta }) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [staticMenuItems, setStaticMenuItems] = useState([]);
  const [projectMenuItems, setProjectMenuItems] = useState([]);
  const [menuLoading, setMenuLoading] = useState(true);
  const [projectLoading, setProjectLoading] = useState(false);
  const [error, setError] = useState(null);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [projectReloadKey, setProjectReloadKey] = useState(0);
  const hasFetchedStaticMenuRef = useRef(false);
  const hasFetchedProjectMenuRef = useRef(false);
  const hasRestoredMenuCacheRef = useRef(false);
  const lastMenuCacheKeyRef = useRef("");
  const menuCacheRef = useRef(new Map());
  const lastFetchedProjectIdRef = useRef(null);
  const menuScrollRef = useRef(null);
  const menuScrollTopRef = useRef(0);
  const navigate = useNavigate();
  useEffect(() => {
    console.log("[SidebarMenu] Sidebar mounted");
  }, []);
  // Fetch projects once on load (and refresh on projectCreated)
  useEffect(() => {
    const fetchProjects = async () => {
      setProjectLoading(true);

      try {
        const response = await axiosInstance.get("/api/get_project");

        const projectData = response.data?.data || [];
        setProjects(projectData);

        const storedProject = JSON.parse(localStorage.getItem("selectedProject"));
        const currentSelected = JSON.parse(
          localStorage.getItem("selectedProject"),
        );

        const hasCurrentSelected =
          currentSelected &&
          projectData.some((p) => p.project_id === currentSelected);
        const hasStoredSelected =
          storedProject && projectData.some((p) => p.project_id === storedProject);

        if (hasCurrentSelected) {
          setSelectedProject(currentSelected);
          localStorage.setItem("selectedProject", JSON.stringify(currentSelected));
        } else if (hasStoredSelected) {
          setSelectedProject(storedProject);
          localStorage.setItem("selectedProject", JSON.stringify(storedProject));
        } else if (projectData.length > 0) {
          const firstId = projectData[0].project_id;
          setSelectedProject(firstId);
          localStorage.setItem("selectedProject", JSON.stringify(firstId));
        } else {
          setSelectedProject(null);
          localStorage.removeItem("selectedProject");
        }

        setError(null);
        setIsInitialized(true);
      } catch (err) {
        console.error("Failed to fetch projects:", err);
        setProjects([]);
        setSelectedProject(null);
        localStorage.removeItem("selectedProject");
        setIsInitialized(true);
      } finally {
        setProjectLoading(false);
      }
    };

    fetchProjects();
  }, [projectReloadKey]);

  useEffect(() => {
    const handleProjectCreated = (e) => {
      console.log("Received projectCreated event:", e);
      setProjectReloadKey((prev) => prev + 1);
    };

    window.addEventListener("projectCreated", handleProjectCreated);

    return () => {
      window.removeEventListener("projectCreated", handleProjectCreated);
    };
  }, []);
 
  const location = useLocation();

  const toggleSidebar = () => {
    setIsExpanded(!isExpanded);
  };

  useEffect(() => {
    onSidebarToggle(isExpanded);
  }, [isExpanded, onSidebarToggle]);

  // Dispatch projectChanged for other pages depending on selected project
  useEffect(() => {
    if (!selectedProject) return;

    const rafId = window.requestAnimationFrame(() => {
      window.dispatchEvent(
        new CustomEvent("projectChanged", {
          detail: { project_id: selectedProject, type: "selected" },
        }),
      );
    });

    return () => window.cancelAnimationFrame(rafId);
  }, [selectedProject]);

  // Keep the dropdown in sync when another view changes the selected project
  // (e.g. opening a flange/equipment from a ticket that lives in a different
  // project). The guard prevents re-dispatch loops with the effect above.
  useEffect(() => {
    const syncSelectedProject = (e) => {
      let next = e?.detail?.project_id;
      if (next == null) {
        try {
          next = JSON.parse(localStorage.getItem("selectedProject"));
        } catch {
          next = null;
        }
      }
      const nextId = String(next?.id || next || "");
      setSelectedProject((prev) => {
        const prevId = String(prev?.id || prev || "");
        return prevId === nextId ? prev : next;
      });
    };

    window.addEventListener("projectChanged", syncSelectedProject);
    window.addEventListener("storage", syncSelectedProject);
    return () => {
      window.removeEventListener("projectChanged", syncSelectedProject);
      window.removeEventListener("storage", syncSelectedProject);
    };
  }, []);

  // Fetch guarded menu data:
  // 1) static menu once
  // 2) project menu once (when selected project becomes available)
  useEffect(() => {
    console.log("[SidebarMenu] effect fired");
    if (!isInitialized) return;

    const token = localStorage.getItem("authKey");
    const userId = authMeta?.userId || localStorage.getItem("user_id") || "";
    const role = authMeta?.role || localStorage.getItem("role_name") || "";
    const companyId = authMeta?.companyId || localStorage.getItem("company_id") || "";
    console.log("[SidebarMenu] token/user/role/selectedProjectId values", {
      hasToken: Boolean(token),
      userId,
      role,
      companyId,
      selectedProjectId: selectedProject,
    });
    if (!token) {
      console.log("[SidebarMenu] skipped reason: missing token");
      setMenuLoading(false);
      return;
    }

    const menuCacheKey = `menu:${userId}:${role}:${companyId}`;
    if (lastMenuCacheKeyRef.current !== menuCacheKey) {
      lastMenuCacheKeyRef.current = menuCacheKey;
      hasRestoredMenuCacheRef.current = false;
      hasFetchedStaticMenuRef.current = false;
      hasFetchedProjectMenuRef.current = false;
      lastFetchedProjectIdRef.current = null;
      setStaticMenuItems([]);
      setProjectMenuItems([]);
      setMenuLoading(true);
    }

    if (!hasRestoredMenuCacheRef.current) {
      const cachedFromMemory = menuCacheRef.current.get(menuCacheKey);
      const cachedFromStorageRaw = sessionStorage.getItem(menuCacheKey);
      let cachedFromStorage = null;
      if (cachedFromStorageRaw) {
        try {
          cachedFromStorage = JSON.parse(cachedFromStorageRaw);
        } catch (e) {
          cachedFromStorage = null;
        }
      }
      const cached = cachedFromMemory || cachedFromStorage;
      if (cached) {
        const nextStatic = cached.static || [];
        const nextProject = cached.project || [];
        setStaticMenuItems(nextStatic);
        setProjectMenuItems(nextProject);
        hasFetchedStaticMenuRef.current =
          typeof cached.hasStaticMenu === "boolean"
            ? cached.hasStaticMenu
            : true;
        hasFetchedProjectMenuRef.current =
          typeof cached.hasProjectMenu === "boolean"
            ? cached.hasProjectMenu
            : nextProject.length > 0;
      }
      hasRestoredMenuCacheRef.current = true;
    }
    
    const needsStaticFetch = !hasFetchedStaticMenuRef.current;
    const needsProjectFetch =
      Boolean(selectedProject) &&
      lastFetchedProjectIdRef.current !== selectedProject;
    
    if (!needsStaticFetch && !needsProjectFetch) {
      const hasStaticMenu = staticMenuItems.length > 0;
      const hasProjectMenu = !selectedProject || projectMenuItems.length > 0;

      if (hasStaticMenu && hasProjectMenu) {
        console.log("[SidebarMenu] skipped reason: menu already fetched with data");
        setMenuLoading(false);
        return;
      }

      console.log("[SidebarMenu] forcing fetch because required menu is missing");
    }

    const fetchMenu = async () => {
      menuScrollTopRef.current = menuScrollRef.current?.scrollTop || 0;
      setMenuLoading(true);
      try {
        const shouldIncludeProjectParam =
          Boolean(selectedProject) && (needsProjectFetch || needsStaticFetch);
        if (shouldIncludeProjectParam) {
          console.log("[SidebarMenu] fetching project menu");
        } else if (needsStaticFetch) {
          console.log("[SidebarMenu] fetching static menu");
        }
        const requestConfig = shouldIncludeProjectParam
          ? { params: { project_id: selectedProject } }
          : {};
        const response = await axiosInstance.get(
          "/access/fetch_menu_details",
          requestConfig,
        );

        const nextStatic = response.data.static || [];
        const nextProject = response.data.project || [];

        setStaticMenuItems(nextStatic);
        if (nextProject.length > 0 || needsProjectFetch) {
          setProjectMenuItems(nextProject);
        }

        // hasFetchedStaticMenuRef.current = true;
        // if (shouldIncludeProjectParam) {
        //   hasFetchedProjectMenuRef.current = true;
        // }
        hasFetchedStaticMenuRef.current = true;

        if (selectedProject) {
          hasFetchedProjectMenuRef.current = true;
          lastFetchedProjectIdRef.current = selectedProject;
        }

        const nextCache = {
          static: nextStatic,
          project:
            nextProject.length > 0 || needsProjectFetch
              ? nextProject
              : projectMenuItems,
          hasStaticMenu: hasFetchedStaticMenuRef.current,
          hasProjectMenu: hasFetchedProjectMenuRef.current,
        };
        menuCacheRef.current.set(menuCacheKey, nextCache);
        sessionStorage.setItem(menuCacheKey, JSON.stringify(nextCache));
        setError(null);

        window.requestAnimationFrame(() => {
          if (menuScrollRef.current) {
            menuScrollRef.current.scrollTop = menuScrollTopRef.current;
          }
        });
      } catch (err) {
        console.error("Failed to fetch menu:", err);

        if (err.response?.status !== 401) {
          setError(err.message);
        }
      } finally {
        setMenuLoading(false);
      }
    };

    fetchMenu();
  }, [isInitialized, selectedProject, authMeta?.userId, authMeta?.role, authMeta?.companyId]);

  const role = localStorage.getItem("role_name")?.toLowerCase();

  const handleProjectChange = useCallback((option) => {
    const selected = option?.value || null;
    if (selected === selectedProject) return;
    setSelectedProject(selected);
    if (selected) {
      localStorage.setItem("selectedProject", JSON.stringify(selected));
    } else {
      localStorage.removeItem("selectedProject");
    }
  }, [selectedProject]);

  const projectOptions = useMemo(
    () =>
      projects.map((project) => ({
        value: project.project_id,
        label: project.name,
      })),
    [projects],
  );
  const selectedProjectOption = useMemo(
    () => projectOptions.find((option) => option.value === selectedProject) || null,
    [projectOptions, selectedProject],
  );
  const projectSelectComponents = useMemo(
    () => ({
      SingleValue: ProjectSingleValue,
      DropdownIndicator: ProjectDropdownIndicator,
      IndicatorSeparator: null,
      LoadingIndicator: null,
    }),
    [],
  );

  if (menuLoading) {
    return (
      <>
        <Header isSidebarExpanded={isExpanded} onLogout={onLogout} />
        <div
          className={`sidebar-container-wrapper ${
            isExpanded ? "expanded" : "collapsed"
          }`}
        >
          <div
            className={`sidebar-container ${
              isExpanded ? "expanded" : "collapsed"
            }`}
            style={{
              display: "flex",
              flexDirection: "column",
              height: "100vh",
              overflow: "hidden",
              position: "fixed",
            }}
          >
            <div className="sidebar-header">
              {isExpanded ? (
                <div className="sidebar-brand-block">
                  <img src="/jecon.png" alt="TAROS logo" className="sidebar-logo" />
                  <div className="sidebar-brand-owner" title="by Jecon Engineers Private Limited">
                    by Jecon Engineers Private Limited
                  </div>
                </div>
              ) : (
                <img src="/favicon.png" alt="TAROS logo" className="sidebar-logo" style={{ width: "32px", height: "32px" }} />
              )}
            </div>
            <div className="menu-items">
              <p>Loading menu...</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (error && staticMenuItems.length === 0 && projectMenuItems.length === 0) {
    return (
      <>
        <Header isSidebarExpanded={isExpanded} onLogout={onLogout} />
        <div
          className={`sidebar-container-wrapper ${
            isExpanded ? "expanded" : "collapsed"
          }`}
        >
          <div
            className={`sidebar-container ${
              isExpanded ? "expanded" : "collapsed"
            }`}
            style={{
              display: "flex",
              flexDirection: "column",
              height: "100vh",
              overflow: "hidden",
              position: "fixed",
            }}
          >
            <div className="sidebar-header">
              {isExpanded ? (
                <div className="sidebar-brand-block">
                  <img src="/jecon.png" alt="TAROS logo" className="sidebar-logo" />
                  <div className="sidebar-brand-owner" title="by Jecon Engineers Private Limited">
                    by Jecon Engineers Private Limited
                  </div>
                </div>
              ) : (
                <img src="/favicon.png" alt="TAROS logo" className="sidebar-logo" style={{ width: "32px", height: "32px" }} />
              )}
            </div>
            <div className="menu-items">
              <p>Error loading menu: {error}</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Header isSidebarExpanded={isExpanded} onLogout={onLogout} />
      <div
        className={`sidebar-container-wrapper ${
          isExpanded ? "expanded" : "collapsed"
        }`}
      >
        <div
          className={`sidebar-container ${
            isExpanded ? "expanded" : "collapsed"
          }`}
          style={{
            display: "flex",
            flexDirection: "column",
            height: "100vh",
            overflow: "hidden",
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            top: 0,
          }}
        >
          <div className="sidebar-header">
            {isExpanded ? (
              <div className="sidebar-brand-block">
                <img src="/jecon.png" alt="TAROS logo" className="sidebar-logo" />
                <div className="sidebar-brand-owner" title="by Jecon Engineers Private Limited">
                  by Jecon Engineers Private Limited
                </div>
              </div>
            ) : (
              <img src="/favicon.png" alt="TAROS logo" className="sidebar-logo" style={{ width: "32px", height: "32px" }} />
            )}
          </div>

          <div className="sidebar-project-tools">
            <div className="sidebar-project-select-wrap">
              <Select
                classNamePrefix="project-select"
                className="project-select"
                title={selectedProjectOption?.label || "Select Project"}
                value={selectedProjectOption}
                onChange={handleProjectChange}
                options={projectOptions}
                isLoading={projectLoading}
                isClearable={false}
                isSearchable={false}
                placeholder={projectLoading ? "Loading projects..." : "Select Project"}
                components={projectSelectComponents}
               
                noOptionsMessage={() =>
                  projectLoading ? "Loading..." : "No projects found"
                }
              />
            </div>

            {role === "admin" && (
              <div className="sidebar-project-action-wrap">
                <button
                  type="button"
                  className="btn btn-secondary btn-sm sidebar-create-project-btn"
                  onClick={() => navigate("/project-form")}
                >
                  <span>+</span>{" "}
                  {isExpanded && <span className="ms-1">Create Project</span>}
                </button>
              </div>
            )}
          </div>

          <div className="scrollbar-arrow-mask-frame sidebar-scrollbar-mask-frame sidebar-scrollbar-mask-frame--menu">
            <nav
              ref={menuScrollRef}
              className="menu-items gap-2 sidebar-scrollable-menu light-scrollbar"
              style={{
                overflowY: "auto",
                overflowX: "hidden",
                flex: 1,
                minHeight: 0,
                marginBottom: "50px",
                paddingBottom: 0,
              }}
            >
            {selectedProject && projectMenuItems.length > 0 && isExpanded && (
              <span className="sidebar-section-label">
                Project Menu
              </span>
            )}

            {(selectedProject ? projectMenuItems : [])
              .filter((item) => {
                if (
                  role === "field technician" &&
                  getMenuName(item).toLowerCase() === "projects"
                ) {
                  return false;
                }
                return true;
              })
              .map((node) => (
                <MenuNode
                  key={node.menu_id}
                  node={node}
                  depth={0}
                  isExpanded={isExpanded}
                  location={location}
                />
              ))}

            <>
              {isExpanded && (
                <span className="sidebar-section-label">
                  Main Menu
                </span>
              )}

              {staticMenuItems
                .filter((item) => getMenuName(item).toLowerCase() !== "my activity")
                .map((node) => (
                  <MenuNode
                    key={node.menu_id}
                    node={node}
                    depth={0}
                    isExpanded={isExpanded}
                    location={location}
                  />
                ))}
            </>
            </nav>
            <div
              aria-hidden="true"
              className="scrollbar-arrow-mask scrollbar-arrow-mask--vertical-top"
            />
            <div
              aria-hidden="true"
              className="scrollbar-arrow-mask scrollbar-arrow-mask--vertical-bottom"
            />
          </div>

          <div
            className="sidebar-footer"
            style={{
              width: isExpanded ? "219px" : "56px",
              transition: "width 0.3s ease-in-out",
              zIndex: 2,
            }}
            onClick={toggleSidebar}
          >
            <div className="expand-sidebar">
              {isExpanded ? <IoIosArrowBack /> : <IoIosArrowForward />}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default Sidebar;
